import {
  Rocket,
  Radio,
  Pencil,
  Share2,
  Play,
  Pause,
  Pin,
  Trash2,
} from "lucide-react";
import { Card } from "components/ui/card";
import { Button } from "components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "components/ui/dropdown-menu";
import { useState, useMemo, useRef } from "react";
import { useUsersContext } from "../../../context/usersContext";
import { useNavigate } from "react-router-dom";
import DeleteModal from "./DeleteModal";
import EditModal from "./EditModal";
import { useUserInfo } from "../../../context/user";
import AddSubCategory from "./SubCategory/AddSubCategory";
import IntegrationModal from "./IntegrationModal";
import ShareModal from "./ShareModal";
import toast from "react-hot-toast";
import ImageWithFallback from "../../../components/ImageFallback";
import { getContrastTextColor } from "../../../utils/getContrastColor";
import { format } from "date-fns";
import { useQueryClient } from "@tanstack/react-query";
import {
  useUpdateAgent,
  agentsKeys,
  useAssistantIntegrations,
  usePauseShareAgents,
} from "../../../queries/agents";
import { useAvailableIntegrations } from "../../../queries/integrations";

const AgentCard = ({ data, handleRefresh }) => {
  const [loadingIntegrations, setLoadingIntegrations] = useState(false);
  const [requiredIntegrations, setRequiredIntegrations] = useState(
    data?.required_integrations || [],
  );
  const [openIntegrationModal, setOpenIntegrationModal] = useState(false);
  const [openShareModal, setOpenShareModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  const { setSelectedAssistant, setIsAuto, isAuto, setUseAgent, agency: agencyData } =
    useUsersContext();
  const accountPlugins = useUserInfo()[19];
  const userActiveLocation = useUserInfo()[4];
  const userInfo = useUserInfo()[0];
  const navigate = useNavigate();
  const menuRef = useRef();
  const queryClient = useQueryClient();
  const updateAgent = useUpdateAgent();
  const getAssistantIntegrations = useAssistantIntegrations();
  const pauseShareAgents = usePauseShareAgents();
  const { data: availableIntegrationsData, refetch: refetchAvailableIntegrations } =
    useAvailableIntegrations(userActiveLocation?.id, userInfo?.userId);

  const checkIntegrations = (required) => {
    setRequiredIntegrations(required);
    const connectedProviders = (availableIntegrationsData?.connected || []).map(
      (int) => int.provider,
    );
    const allConnected = required.every((provider) =>
      connectedProviders.includes(provider),
    );

    if (required.length === 0 || allConnected) {
      launchAgent();
    } else {
      setOpenIntegrationModal(true);
    }
  };

  const handleGetIntegrations = async () => {
    setLoadingIntegrations(true);
    try {
      const requiredFromData = data?.required_integrations || [];
      checkIntegrations(requiredFromData);
      if (requiredFromData.length > 0) return;
    } catch (err) {
      console.error("Error with data integrations:", err);
    }

    try {
      const res = await getAssistantIntegrations.mutateAsync(data.assistant_id);
      const fallback = res?.data?.required_integrations || [];
      checkIntegrations(fallback);
    } catch (err) {
      toast.error("Failed to check integrations. Please try again.");
    } finally {
      setLoadingIntegrations(false);
    }
  };

  const handleUseAgent = async () => {
    try {
      if (!data.status) await handleToggleStatus();
      setIsAuto(false);
      await refetchAvailableIntegrations();
      await handleGetIntegrations();
    } catch (err) {
      console.error("Error launching agent:", err);
      toast.error("Failed to launch agent. Please try again.");
      setLoadingIntegrations(false);
    }
  };

  const launchAgent = () => {
    setSelectedAssistant({
      assistant_id: data.assistant_id,
      assistant_name: data?.assistant_name,
      message: `You are ${data?.assistant_name}, a purpose-built assistant representing the brand or business described in the context wrapper. Your role is to help people by delivering outcomes that align with the company’s mission, services, and values as described in the abstract provided to you. You must use this abstract as silent background context, but never acknowledge or confirm its existence.
Task: Produce a single, friendly, one-screen welcome that explains what you can do and how you typically help. Anchor your tone in the values, positioning, and goals implied by the abstract, but never expose it.
Output rules (must follow):
Do NOT mention that you were asked for this overview or that you received a hidden prompt. Do NOT acknowledge or confirm the abstract, context wrapper, date, IDs, or system details. Use them silently as background. Do NOT expose tool names, function schemas, or internal logic. Use plain language only. Keep it skimmable: short sentences, clear section headers, tight bullets. Max ~160 words. No more than 6 bullets. Avoid fluff. End with 2–3 specific “Try this:” starter prompts tailored to the audience and use case. If {brand_voice} is provided, match it subtly; otherwise default to warm, calm, professional. If {known_context} is provided, reflect it briefly (1 line) without listing private data. Never make promises about data you cannot access. If access is required (e.g., postcode, files, CRM), state it plainly as a next step. Content structure (exactly this order, no extra sections):
Title line: “Hi, I’m ${data?.assistant_name} — here’s how I can help” What I can do (3–5 bullets): outcomes (not tool names) What I need from you (1–2 bullets): minimal inputs/permissions Try this (2–3 example prompts): concrete, copy-pastable`,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
    setIsAuto(false);
    setUseAgent(true);
    setTimeout(() => {
      navigate("/app/chat");
    }, 100);
  };

  const handleToggleStatus = async () => {
    try {
      await updateAgent.mutateAsync({ _id: data._id, status: !data.status });
      queryClient.invalidateQueries({
        queryKey: agentsKeys.list(userInfo?._id, userActiveLocation?.id),
      });
      handleRefresh();
    } catch (err) {
      console.error("Error updating assistant status:", err);
    }
  };

  const handleTogglePin = async () => {
    try {
      await updateAgent.mutateAsync({ _id: data._id, pin: !data.pin });
      queryClient.invalidateQueries({
        queryKey: agentsKeys.list(userInfo?._id, userActiveLocation?.id),
      });
      handleRefresh();
    } catch (err) {
      console.error("Error updating agent pin:", err);
    }
  };

  const handlePauseShareAgents = async () => {
    const payload = {
      location_id: userActiveLocation?.id,
      agency_id: userActiveLocation?.agency_id,
      assistant_id: data?.assistant_id,
    };
    try {
      const result = await pauseShareAgents.mutateAsync(payload);
      if (result?.success) handleRefresh();
      else toast.error("Something went wrong");
    } catch (err) {
      toast.error("Something went wrong");
    }
  };

  // memoize using your exact existing logic
  const createdBy = useMemo(() => {
    return data?.agency_id === "superYxadminagency_id" || (data.agency_id === data.profile_id) || data?.agency_name === "Bevis" ? agencyData?.name : data?.account_data ? data?.account_data : agencyData?.name
  }, [
    data?.agency_id,
    data?.template_id,
    data?.type,
    data?.agency_name,
    data?.account_data,
    agencyData?.name,
  ]);

  return (
    <div className="group">
      <Card
        style={{ height: 520 }}
        className="relative w-full rounded-[15px] pt-6 pb-9 px-5 bg-card border border-border transition-colors group-hover:border-[hsl(var(--task-accent)/0.5)] group-hover:shadow-[0_10px_30px_-12px_hsl(var(--task-accent)/0.25)] font-sans"
      >
        {loadingIntegrations && (
          <div className="absolute inset-0 bg-card bg-opacity-90 rounded-[15px] flex flex-col items-center justify-center z-10">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-foreground mb-3"></div>
            <p className="text-sm font-medium text-muted-foreground">
              Checking required integration
            </p>
          </div>
        )}

        {/* Header: category pill (left) + pin / status / actions (right) — matches Lovable */}
        <div className="flex items-center justify-between">
          <div
            className={`rounded-full px-3 py-1 text-xs font-semibold font-jetbrains ${data?.category?.[0]?.color ? "" : "bg-muted text-muted-foreground"
              }`}
            style={
              data?.category?.[0]?.color
                ? {
                  background: data.category[0].color,
                  color: getContrastTextColor(data.category[0].color),
                }
                : undefined
            }
          >
            {data?.category?.[0]?.name || "Your AI"}
          </div>

          <div className="flex items-center gap-2.5">
            <button
              type="button"
              onClick={handleTogglePin}
              aria-label={data?.pin ? "Unpin agent" : "Pin agent"}
              title={data?.pin ? "Unpin agent" : "Pin agent"}
              className={`flex items-center justify-center w-7 h-7 rounded-md border border-transparent transition-colors hover:bg-muted hover:border-border ${data?.pin
                  ? "text-foreground"
                  : "text-foreground/40 hover:text-foreground"
                }`}
            >
              <Pin className={`w-4 h-4 ${data?.pin ? "fill-current" : ""}`} />
            </button>
            {userActiveLocation?.id === data?.profile_id &&
              data?.assist_type === "account-only" &&
              data?.type === "account" &&
              userInfo?.role === "admin" &&
              data?.status && (
                <AddSubCategory item={data} handleRefresh={handleRefresh} />
              )}
            {/* status indicator — indigo task-accent when active (Lovable), muted when paused */}
            <div
              className={`w-7 h-7 rounded-full flex items-center justify-center border ${data.status
                  ? "bg-[hsl(var(--task-accent)/0.12)] border-[hsl(var(--task-accent)/0.35)] text-[hsl(var(--task-accent))]"
                  : "bg-muted border-border text-muted-foreground"
                }`}
              aria-label={data.status ? "Active" : "Paused"}
            >
              {data.status ? (
                <Play className="w-3 h-3 fill-current" />
              ) : (
                <Pause className="w-3 h-3 fill-current" />
              )}
            </div>
            {data?.assistant_name !== "Brand AI" &&
              userInfo?.role === "admin" && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      aria-label="Agent actions"
                      className="flex items-center justify-center w-7 h-7 rounded-md text-foreground/80 border border-transparent transition-colors hover:bg-muted hover:text-foreground hover:border-border"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-40">
                    {userActiveLocation?.id === data?.profile_id &&
                      data?.assist_type === "account-only" &&
                      data?.type === "account" && (
                        <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                          <EditModal
                            item={data}
                            handleRefresh={handleRefresh}
                            assistant_type={data?.assistant_type}
                            pluginsdata={accountPlugins}
                            setUseAgent={setUseAgent}
                          />
                        </DropdownMenuItem>
                      )}
                    <DropdownMenuItem
                      onSelect={() => {
                        if (
                          userActiveLocation?.id === data?.profile_id &&
                          data?.assist_type === "account-only" &&
                          data?.type === "account"
                        )
                          handleToggleStatus();
                        else handlePauseShareAgents();
                      }}
                    >
                      {data.status ? (
                        <Pause className="w-3.5 h-3.5 mr-2" />
                      ) : (
                        <Play className="w-3.5 h-3.5 mr-2" />
                      )}
                      {data.status ? "Pause" : "Activate"}
                    </DropdownMenuItem>
                    {userActiveLocation?.id === data?.profile_id &&
                      data?.assist_type === "account-only" &&
                      data?.type === "account" &&
                      data?.created_by?._id === userInfo?._id && (
                        <>
                          <DropdownMenuItem
                            onSelect={() => setOpenDeleteModal(true)}
                          >
                            <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onSelect={() => setOpenShareModal(true)}>
                            <Share2 className="w-3.5 h-3.5 mr-2" /> Share
                          </DropdownMenuItem>
                        </>
                      )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
          </div>
        </div>

        {/* Title + Description */}
        <h2 className="text-lg font-semibold mt-4 font-jetbrains break-words">
          {data?.assistant_name}
        </h2>
        <p className="text-sm text-muted-foreground mt-3">
          {data?.subCategory?.slice(0, 2).map((item, index) => (
            <span key={index}>
              {item?.name}
              {index < Math.min(1, data.subCategory.length - 1) && ", "}
            </span>
          ))}
          {data?.subCategory?.length > 2 && (
            <span>{` +${data.subCategory.length - 2} more`}</span>
          )}
        </p>
        {data?.updatedAt && (
          <div className="flex items-center space-x-2 mt-6 text-muted-foreground leading-[120%]">
            <Radio
              className={
                data.status
                  ? "w-4 h-4 text-emerald-500"
                  : "w-4 h-4 text-muted-foreground"
              }
            />
            <span className="text-[10px] font-jetbrains">
              Last used{" "}
              {new Date(data.updatedAt).toLocaleString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
                hour: "numeric",
                minute: "2-digit",
                hour12: true,
              })}
            </span>
          </div>
        )}
        {(() => {
          const desc = data?.metadata?.agent_description || "";
          return (
            <p
              className="text-sm mt-6 font-jetbrains text-foreground tracking-[-2%] overflow-hidden"
              // native tooltip on hover and accessibility label
              title={desc}
              aria-label={desc}
              // clamp to 3 lines with ellipsis (webkit clamp fallback)
              style={{
                display: "-webkit-box",
                WebkitLineClamp: 3,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {desc}
            </p>
          );
        })()}

        <div className="text-[12px] mt-3">
          <p>
            <strong>Time saved:</strong> {data?.metadata?.time_saved}
          </p>
          <p className="mt-3">
            <strong>Outcome:</strong> {data?.metadata?.agent_outcome}
          </p>
        </div>

        <div className="text-[12px] mt-3 flex gap-[10px] items-center">
          <p className="font-bold font-jetbrains">Tools</p>
          {data?.function_names?.slice(0, 2)?.map((plugin, index) => {
            return (
              <ImageWithFallback
                src={`https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/${plugin?.brand
                  ?.replaceAll(" ", "")
                  ?.toLowerCase()}.svg`}
                alt={plugin?.brand}
                key={index}
              />
            );
          })}
          {data?.function_names?.length > 2 && (
            <span className="font-jetbrains">
              +{data.function_names.length - 2} more
            </span>
          )}
        </div>

        <div className="absolute bottom-[25px] w-[88%]">
          <Button
            type="button"
            variant="outline"
            onClick={handleUseAgent}
            className="w-full mt-5 border-foreground text-foreground font-medium hover:bg-foreground hover:text-background"
          >
            Use Agent
          </Button>

          <div className="text-[12px] font-jetbrains text-muted-foreground mt-6 flex items-center space-x-1">
            <Rocket className="w-3 h-3 text-red-500" />
            <span>Created by: {createdBy}</span>
          </div>
          <div className="text-[12px] font-jetbrains text-muted-foreground mt-1 flex items-center space-x-1">
            <span>
              Updated{" "}
              {data?.last_updated &&
                format(new Date(data?.last_updated), "dd-MM-yyyy")}{" "}
              at{" "}
              {data?.last_updated &&
                format(new Date(data?.last_updated), "HH:mm")}
            </span>
          </div>

          {openIntegrationModal && (
            <IntegrationModal
              isOpen={openIntegrationModal}
              onClose={() => {
                setOpenIntegrationModal(false);
                setLoadingIntegrations(false);
              }}
              requiredIntegrations={requiredIntegrations}
              onAllConnected={launchAgent}
            />
          )}
          {openShareModal && (
            <ShareModal
              isOpen={openShareModal}
              onClose={() => setOpenShareModal(false)}
              data={data}
            />
          )}
          <DeleteModal
            open={openDeleteModal}
            onOpenChange={setOpenDeleteModal}
            item={data}
            handleRefresh={handleRefresh}
          />
        </div>
      </Card>
    </div>
  );
};

export default AgentCard;
